// src/tabs/ModelagemSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Trash2, Edit3, Link, FileText, Upload, CheckCircle2, AlertCircle, LayoutGrid, List, Search, X } from "lucide-react";
import { pb } from "../services/pocketbase";
import { produtoDadosService } from "../services/produtoDadosService";

const HEADER_STYLES = {
  pendente_aprovacao: "bg-slate-500 border-slate-600",
  solicitado: "bg-blue-600 border-blue-700",
  em_revisao: "bg-amber-600 border-amber-700",
  aguardando_anuncio: "bg-purple-600 border-purple-700",
  anunciado: "bg-emerald-600 border-emerald-700",
  rejeitado: "bg-red-650 border-red-750"
};

const getArquivosArray = (arquivos) => {
  if (!arquivos) return [];
  if (Array.isArray(arquivos)) return arquivos;
  if (typeof arquivos === "string") {
    try {
      const parsed = JSON.parse(arquivos);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      if (arquivos.includes(",")) {
        return arquivos.split(",").map(s => s.trim()).filter(Boolean);
      }
    }
    return [arquivos];
  }
  return [];
};

export default function ModelagemSubTab({ user, setActiveSubTab, externalSearchSku }) {
  const [modelings, setModelings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list"); // default to list

  const columns = useMemo(() => {
    const cols = [
      { id: "pendente_aprovacao", label: "Pendente de Aprovação", color: "bg-slate-100 text-slate-500 border-slate-200" },
      { id: "em_revisao", label: "Em Revisão", color: "bg-amber-100 text-amber-800 border-amber-200" },
      { id: "solicitado", label: "Solicitado", color: "bg-blue-100 text-blue-800 border-blue-200" },
      { id: "aguardando_anuncio", label: "Aguardando Anúncio", color: "bg-purple-100 text-purple-800 border-purple-200" },
      { id: "anunciado", label: "Anunciado", color: "bg-emerald-100 text-emerald-800 border-emerald-200" }
    ];
    if (user?.role === "master") {
      cols.push({ id: "rejeitado", label: "Rejeitado", color: "bg-red-100 text-red-800 border-red-200" });
    }
    return cols;
  }, [user]);

  // Filters State
  const [search, setSearch] = useState("");
  const [filterTipoJoia, setFilterTipoJoia] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterMercadoLivre, setFilterMercadoLivre] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");

  useEffect(() => {
    if (externalSearchSku) {
      setSearch(externalSearchSku);
      setFilterStatus("all");
    }
  }, [externalSearchSku]);

  const prioritiesMatch = useCallback((p1, p2) => {
    if (!p1 || !p2) return false;
    const a = p1.toLowerCase().trim();
    const b = p2.toLowerCase().trim();
    if (a === b) return true;
    const isAMedia = (a === "media" || a === "média" || a === "medio" || a === "médio");
    const isBMedia = (b === "media" || b === "média" || b === "medio" || b === "médio");
    return isAMedia && isBMedia;
  }, []);

  const getPriorityWeight = useCallback((priority) => {
    if (!priority) return 2; // Default to media
    const p = priority.toLowerCase().trim();
    if (p === "urgente") return 4;
    if (p === "alta") return 3;
    if (p === "media" || p === "média" || p === "medio" || p === "médio") return 2;
    if (p === "baixa") return 1;
    return 2; // Default to media
  }, []);

  const filteredModelings = useMemo(() => {
    const hasSearch = !!search.trim();
    const anunciadoIds = modelings
      .filter(item => item.status === "anunciado")
      .slice(0, hasSearch ? modelings.length : 30)
      .map(item => item.id);

    const filtered = modelings.filter(item => {
      // 0. Visibilidade de itens para modelista / outros roles
      if (user?.role === "modelista") {
        const isAuthorizedStatus = ["solicitado", "em_revisao", "aguardando_anuncio", "anunciado"].includes(item.status);
        const isOwner = item.responsavel === user.id;
        if (!isAuthorizedStatus && !isOwner) {
          return false;
        }
      } else if (user?.role !== "master" && item.status === "rejeitado") {
        return false;
      }

      // Limit anunciado (unless searching)
      if (item.status === "anunciado" && !anunciadoIds.includes(item.id)) {
        return false;
      }

      // 1. Search Query
      if (search.trim()) {
        const query = search.toLowerCase().trim();
        const matchesSku = item.sku?.toLowerCase().includes(query);
        const matchesNome = item.nome_produto?.toLowerCase().includes(query);
        const matchesResp = item.responsavel?.toLowerCase().includes(query);
        const matchesObs = item.observacoes?.toLowerCase().includes(query);
        if (!matchesSku && !matchesNome && !matchesResp && !matchesObs) {
          return false;
        }
      }

      // 2. Tipo de Joia Filter
      if (filterTipoJoia !== "all") {
        const itemTipo = item.tipo_joia || "Anel";
        if (itemTipo !== filterTipoJoia) return false;
      }

      // 3. Category Filter
      if (filterCategory !== "all") {
        if (item.categoria !== filterCategory) return false;
      }

      // 4. Mercado Livre Filter
      if (filterMercadoLivre !== "all") {
        const isMl = filterMercadoLivre === "yes";
        if (!!item.mercado_livre !== isMl) return false;
      }

      // 5. Status Filter
      if (filterStatus !== "all") {
        if (item.status !== filterStatus) return false;
      }

      // 6. Priority Filter
      if (filterPriority !== "all") {
        if (!prioritiesMatch(item.prioridade || "media", filterPriority)) return false;
      }

      return true;
    });

    // Sort by priority weight (urgente: 4, alta: 3, media: 2, baixa: 1)
    filtered.sort((a, b) => {
      const weightA = getPriorityWeight(a.prioridade);
      const weightB = getPriorityWeight(b.prioridade);
      if (weightA !== weightB) {
        return weightB - weightA;
      }
      const dateA = a.created ? new Date(a.created).getTime() : 0;
      const dateB = b.created ? new Date(b.created).getTime() : 0;
      return dateB - dateA;
    });

    return filtered;
  }, [modelings, search, filterTipoJoia, filterCategory, filterMercadoLivre, filterStatus, filterPriority, user, prioritiesMatch, getPriorityWeight]);

  // Count items for each status dynamically based on current search & filters (except status filter)
  const statusCounts = useMemo(() => {
    const counts = {
      pendente_aprovacao: 0,
      em_revisao: 0,
      solicitado: 0,
      aguardando_anuncio: 0,
      anunciado: 0,
      rejeitado: 0
    };

    const hasSearch = !!search.trim();
    const anunciadoIds = modelings
      .filter(item => item.status === "anunciado")
      .slice(0, hasSearch ? modelings.length : 30)
      .map(item => item.id);

    modelings.forEach(item => {
      // 0. Visibilidade de itens para modelista / outros roles
      if (user?.role === "modelista") {
        const isAuthorizedStatus = ["solicitado", "em_revisao", "aguardando_anuncio", "anunciado"].includes(item.status);
        const isOwner = item.responsavel === user.id;
        if (!isAuthorizedStatus && !isOwner) {
          return;
        }
      } else if (user?.role !== "master" && item.status === "rejeitado") {
        return;
      }

      // Limit anunciado (unless searching)
      if (item.status === "anunciado" && !anunciadoIds.includes(item.id)) {
        return;
      }

      // 1. Search Query
      if (search.trim()) {
        const query = search.toLowerCase().trim();
        const matchesSku = item.sku?.toLowerCase().includes(query);
        const matchesNome = item.nome_produto?.toLowerCase().includes(query);
        const matchesResp = item.responsavel?.toLowerCase().includes(query);
        const matchesObs = item.observacoes?.toLowerCase().includes(query);
        if (!matchesSku && !matchesNome && !matchesResp && !matchesObs) {
          return;
        }
      }

      // 2. Tipo de Joia Filter
      if (filterTipoJoia !== "all") {
        const itemTipo = item.tipo_joia || "Anel";
        if (itemTipo !== filterTipoJoia) return;
      }

      // 3. Category Filter
      if (filterCategory !== "all" && item.categoria !== filterCategory) return;

      // 4. Mercado Livre Filter
      if (filterMercadoLivre !== "all") {
        const isMl = filterMercadoLivre === "yes";
        if (!!item.mercado_livre !== isMl) return;
      }

      // 5. Priority Filter
      if (filterPriority !== "all") {
        if (!prioritiesMatch(item.prioridade || "media", filterPriority)) return;
      }

      if (counts[item.status] !== undefined) {
        counts[item.status]++;
      }
    });

    return counts;
  }, [modelings, search, filterTipoJoia, filterCategory, filterMercadoLivre, filterPriority, user, prioritiesMatch]);

  const totalCount = useMemo(() => {
    return Object.values(statusCounts).reduce((a, b) => a + b, 0);
  }, [statusCounts]);

  const tabsList = useMemo(() => {
    const list = [
      { id: "pendente_aprovacao", label: "Pendente de Aprovação", count: statusCounts.pendente_aprovacao, colorClass: "slate" },
      { id: "em_revisao", label: "Em Revisão", count: statusCounts.em_revisao, colorClass: "amber" },
      { id: "solicitado", label: "Solicitado", count: statusCounts.solicitado, colorClass: "blue" },
      { id: "aguardando_anuncio", label: "Aguardando Anúncio", count: statusCounts.aguardando_anuncio, colorClass: "purple" },
      { id: "anunciado", label: "Anunciado", count: statusCounts.anunciado, colorClass: "emerald", extraLabel: "ultimos 30" }
    ];
    if (user?.role === "master") {
      list.push({ id: "rejeitado", label: "Rejeitado", count: statusCounts.rejeitado, colorClass: "red" });
    }
    return list;
  }, [statusCounts, user]);

  const getTabStyle = useCallback((tabId, colorClass) => {
    const isActive = filterStatus === tabId;
    
    // Base styles
    const base = "relative px-3.5 py-2 rounded-xl border text-[10px] font-black transition-all duration-200 flex items-center gap-2 cursor-pointer select-none active:scale-95 shadow-sm";
    
    if (colorClass === "slate") {
      return isActive 
        ? `${base} bg-slate-600 text-white border-slate-700 shadow-md shadow-slate-600/10 hover:bg-slate-700`
        : `${base} bg-white text-slate-750 border-slate-200 hover:bg-slate-50 hover:border-slate-300`;
    }
    if (colorClass === "amber") {
      return isActive
        ? `${base} bg-amber-500 text-white border-amber-600 shadow-md shadow-amber-500/10 hover:bg-amber-600`
        : `${base} bg-white text-amber-700 border-amber-200 hover:bg-amber-50/50 hover:border-amber-300`;
    }
    if (colorClass === "blue") {
      return isActive
        ? `${base} bg-blue-600 text-white border-blue-700 shadow-md shadow-blue-600/10 hover:bg-blue-700`
        : `${base} bg-white text-blue-700 border-blue-200 hover:bg-blue-50/50 hover:border-blue-300`;
    }
    if (colorClass === "purple") {
      return isActive
        ? `${base} bg-purple-600 text-white border-purple-700 shadow-md shadow-purple-600/10 hover:bg-purple-700`
        : `${base} bg-white text-purple-700 border-purple-200 hover:bg-purple-50/50 hover:border-purple-300`;
    }
    if (colorClass === "emerald") {
      return isActive
        ? `${base} bg-emerald-600 text-white border-emerald-700 shadow-md shadow-emerald-600/10 hover:bg-emerald-700`
        : `${base} bg-white text-emerald-700 border-emerald-250 hover:bg-emerald-50/50 hover:border-emerald-355`;
    }
    if (colorClass === "red") {
      return isActive
        ? `${base} bg-rose-600 text-white border-rose-700 shadow-md shadow-rose-600/10 hover:bg-rose-700`
        : `${base} bg-white text-rose-700 border-rose-200 hover:bg-rose-50/50 hover:border-rose-300`;
    }
    
    // default/todos
    return isActive
      ? `${base} bg-slate-800 text-white border-slate-900 shadow-md hover:bg-slate-900`
      : `${base} bg-white text-slate-500 border-slate-200 hover:bg-slate-50 hover:border-slate-300`;
  }, [filterStatus]);

  // Modal create/edit
  const [isOpen, setIsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    sku: "",
    nome_produto: "",
    tipo_joia: "Anel",
    categoria: "Formatura",
    modelo: "",
    status: "pendente_aprovacao",
    link_produto: "",
    mercado_livre: false,
    responsavel: "",
    prioridade: "media",
    observacoes: "",
    arquivos: null
  });

  // Load modelings
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const records = await pb.collection("modelagens").getFullList({ 
        sort: "-created",
        expand: "responsavel"
      });
      setModelings(records);
    } catch (e) {
      console.error("Erro loading modelagens:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenNew = () => {
    setEditingItem(null);
    const protoSku = "PRT-" + Math.floor(100000 + Math.random() * 900000);
    setForm({
      sku: protoSku,
      nome_produto: "",
      tipo_joia: "Anel",
      categoria: "Formatura",
      modelo: "",
      status: "pendente_aprovacao",
      link_produto: "",
      mercado_livre: false,
      responsavel: user?.id || "",
      prioridade: "media",
      observacoes: "",
      arquivos: null
    });
    setIsOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      sku: item.sku,
      nome_produto: item.nome_produto,
      tipo_joia: item.tipo_joia || item.categoria || "Anel",
      categoria: item.categoria || "Formatura",
      modelo: item.modelo,
      status: item.status,
      link_produto: item.link_produto,
      mercado_livre: item.mercado_livre,
      responsavel: item.responsavel,
      prioridade: item.prioridade || "media",
      observacoes: item.observacoes,
      arquivos: null // files cannot be populated directly back into file inputs
    });
    setIsOpen(true);
  };

  const handleSave = async (e, statusOverride) => {
    if (e) e.preventDefault();
    if (!form.sku.trim()) return alert("Digite o SKU.");
    if (!form.nome_produto.trim()) return alert("Digite o nome do produto.");

    const finalStatus = statusOverride || form.status;

    // Validação de SKU definitivo ao aprovar via modal
    if (finalStatus === "solicitado") {
      const skuVal = form.sku.trim().toUpperCase();
      if (!skuVal || skuVal.startsWith("PRT-")) {
        return alert("O SKU definitivo é obrigatório para aprovação e não pode começar com 'PRT-'.");
      }
    }

    // PocketBase handles file uploads using FormData
    const formDataObj = new FormData();
    formDataObj.append("sku", form.sku.toUpperCase().trim());
    formDataObj.append("nome_produto", form.nome_produto.trim());
    formDataObj.append("tipo_joia", form.tipo_joia);
    formDataObj.append("categoria", form.categoria);
    formDataObj.append("modelo", form.modelo.trim());
    formDataObj.append("status", finalStatus);
    formDataObj.append("link_produto", form.link_produto.trim());
    formDataObj.append("mercado_livre", form.mercado_livre);
    formDataObj.append("responsavel", form.responsavel.trim());
    formDataObj.append("prioridade", form.prioridade);
    formDataObj.append("observacoes", form.observacoes.trim());

    if (form.arquivos) {
      const fieldKey = editingItem ? "arquivos+" : "arquivos";
      for (const file of form.arquivos) {
        formDataObj.append(fieldKey, file);
      }
    }

    try {
      let savedRecord = null;
      if (editingItem) {
        savedRecord = await pb.collection("modelagens").update(editingItem.id, formDataObj);
      } else {
        savedRecord = await pb.collection("modelagens").create(formDataObj);
      }

      // Sincroniza o link_produto com a coleção produto_dados
      if (savedRecord) {
        const skuVal = savedRecord.sku;
        try {
          const pd = await produtoDadosService.getProdutoDados(skuVal);
          if (pd) {
            const currentLinks = pd.links_legado || {};
            if (currentLinks.storeUrl !== savedRecord.link_produto) {
              await pb.collection("produto_dados").update(pd.id, {
                links_legado: {
                  ...currentLinks,
                  storeUrl: savedRecord.link_produto
                }
              });
            }
          }
        } catch (syncErr) {
          console.warn("Falha ao sincronizar link para produto_dados:", syncErr);
        }
      }

      setIsOpen(false);
      loadData();
      alert("Modelagem salva com sucesso!");
    } catch (err) {
      alert("Erro ao salvar modelagem: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja deletar esta modelagem?")) return;
    try {
      await pb.collection("modelagens").delete(id);
      setModelings(prev => prev.filter(m => m.id !== id));
      alert("Modelagem removida.");
    } catch (err) {
      alert("Erro ao remover: " + err.message);
    }
  };

  const updateStatus = async (id, newStatus) => {
    const item = modelings.find(m => m.id === id);
    if (!item) return;

    let finalSku = item.sku;

    // Se estiver aprovando (mudando para "solicitado"), exige SKU definitivo que não comece com PRT-
    if (newStatus === "solicitado" && (item.status === "pendente_aprovacao" || item.status === "rejeitado" || item.sku.startsWith("PRT-"))) {
      let definitiveSku = "";
      while (true) {
        const input = prompt(`Aprovar Modelagem: Digite o SKU definitivo do produto (não pode iniciar com PRT-):`, item.sku.startsWith("PRT-") ? "" : item.sku);
        if (input === null) return; // Cancelado
        const cleaned = input.trim().toUpperCase();
        if (!cleaned) {
          alert("O SKU é obrigatório para aprovação.");
          continue;
        }
        if (cleaned.startsWith("PRT-")) {
          alert("O SKU definitivo não pode começar com 'PRT-'.");
          continue;
        }
        definitiveSku = cleaned;
        break;
      }
      finalSku = definitiveSku;
    }

    try {
      const updated = await pb.collection("modelagens").update(id, { status: newStatus, sku: finalSku });

      // Sincroniza o status operacional CAD com estado_modelagem em produto_dados
      try {
        const pdRecs = await pb.collection("produto_dados").getFullList({ filter: `sku_pai = "${finalSku}"` });
        if (pdRecs && pdRecs.length > 0) {
          const pd = pdRecs[0];
          let updatedEstado = null;
          if (newStatus === "anunciado") updatedEstado = "publicado";
          else if (newStatus === "solicitado" && pd.estado_modelagem === "sem_modelo") updatedEstado = "em_modelagem";

          if (updatedEstado) {
            await pb.collection("produto_dados").update(pd.id, { estado_modelagem: updatedEstado });
          }
        }
      } catch (syncErr) {
        console.warn("Falha ao sincronizar status operacional CAD para produto_dados:", syncErr);
      }

      setModelings(prev => prev.map(m => m.id === id ? updated : m));
      if (newStatus === "solicitado") {
        alert("Modelagem aprovada com SKU: " + finalSku);
      }
    } catch (err) {
      alert("Erro ao atualizar status: " + err.message);
    }
  };

  const handleToggleCatalogLink = async (item) => {
    const nextVal = !item.produto_cadastrado;
    const skuCode = nextVal ? prompt("Digite o SKU Comercial final para vincular:", item.sku) : "";
    
    if (nextVal && !skuCode) return; // cancelled

    try {
      const updated = await pb.collection("modelagens").update(item.id, {
        produto_cadastrado: nextVal,
        sku_catalogo: skuCode || ""
      });
      setModelings(prev => prev.map(m => m.id === item.id ? updated : m));
      alert(nextVal ? "Produto vinculado ao catálogo!" : "Vínculo removido.");
    } catch (err) {
      alert("Erro: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-500 bg-slate-50 h-full">
        <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mr-2"></div>
        Carregando painel de modelagens...
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-50 overflow-hidden text-xs">
      
      {/* HEADER CONTROLS */}
      <div className="bg-white p-4 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3 shrink-0">
        <div>
          <h2 className="font-extrabold text-slate-800 text-sm">Modelagem CAD & Renders 3D</h2>
          <p className="text-[10px] text-slate-400">Gerenciamento e acompanhamento do fluxo de modelagem e aprovação de novas joias.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-slate-100 rounded-lg p-0.5 flex border border-slate-250">
            <button
              onClick={() => setViewMode("kanban")}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === "kanban" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
              }`}
              title="Visualização Kanban"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === "list" ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
              }`}
              title="Visualização Tabela"
            >
              <List size={15} />
            </button>
          </div>

          <button
            onClick={handleOpenNew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-bold flex items-center gap-1.5 shadow-sm"
          >
            <Plus size={14} /> Solicitar Modelagem
          </button>
        </div>
      </div>

      {/* FILTERS BAR */}
      <div className="bg-white px-6 py-3 border-b border-slate-200 flex flex-col md:flex-row gap-4 items-center shrink-0">
        {/* Search Input */}
        <div className="relative w-full md:w-80">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
            <Search size={14} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por SKU, nome, resp..."
            className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white text-xs font-semibold transition-all"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Filters Grid */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Tipo de Joia */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tipo:</span>
            <select
              value={filterTipoJoia}
              onChange={(e) => setFilterTipoJoia(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold text-slate-700 bg-white cursor-pointer hover:bg-slate-100/50 transition-colors"
            >
              <option value="all">Todos</option>
              <option value="Anel">Anel</option>
              <option value="Brinco">Brinco</option>
              <option value="Colar">Colar</option>
              <option value="Pulseira">Pulseira</option>
              <option value="Pingente">Pingente</option>
              <option value="Tornozeleira">Tornozeleira</option>
              <option value="Outro">Outro</option>
            </select>
          </div>

          {/* Categoria */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cat:</span>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold text-slate-700 bg-white cursor-pointer hover:bg-slate-100/50 transition-colors"
            >
              <option value="all">Todas</option>
              <option value="Formatura">Formatura</option>
              <option value="Pet">Pet</option>
              <option value="Coleção">Coleção</option>
              <option value="Sacro">Sacro</option>
              <option value="Espiritualista">Espiritualista</option>
              <option value="Outros">Outros</option>
            </select>
          </div>

          {/* Mercado Livre */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">M. Livre:</span>
            <select
              value={filterMercadoLivre}
              onChange={(e) => setFilterMercadoLivre(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-505 text-xs font-bold text-slate-705 bg-white cursor-pointer hover:bg-slate-100/50 transition-colors"
            >
              <option value="all">Todos</option>
              <option value="yes">Sim</option>
              <option value="no">Não</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-505 text-xs font-bold text-slate-705 bg-white cursor-pointer hover:bg-slate-100/50 transition-colors"
            >
              <option value="all">Todos</option>
              <option value="pendente_aprovacao">Pendente de Aprovação</option>
              <option value="em_revisao">Em Revisão</option>
              <option value="solicitado">Solicitado</option>
              <option value="aguardando_anuncio">Aguardando Anúncio</option>
              <option value="anunciado">Anunciado</option>
              {user?.role === "master" && (
                <option value="rejeitado">Rejeitado</option>
              )}
            </select>
          </div>

          {/* Prioridade */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Prioridade:</span>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-xs font-bold text-slate-700 bg-white cursor-pointer hover:bg-slate-100/50 transition-colors"
            >
              <option value="all">Todas</option>
              <option value="urgente">Urgente</option>
              <option value="alta">Alta</option>
              <option value="media">Média</option>
              <option value="baixa">Baixa</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(search || filterTipoJoia !== "all" || filterCategory !== "all" || filterMercadoLivre !== "all" || filterStatus !== "all" || filterPriority !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setFilterTipoJoia("all");
                setFilterCategory("all");
                setFilterMercadoLivre("all");
                setFilterStatus("all");
                setFilterPriority("all");
              }}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-650 rounded-xl font-extrabold text-[10px] uppercase tracking-wider transition-all"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* STATUS TABS */}
      <div className="bg-white px-6 pb-3 pt-5 border-b border-slate-200 flex flex-wrap items-center gap-2.5 shrink-0 select-none">
        <button
          onClick={() => setFilterStatus("all")}
          className={getTabStyle("all", "todos")}
        >
          <span>TODOS</span>
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-extrabold ${
            filterStatus === "all" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
          }`}>
            {totalCount}
          </span>
        </button>

        {tabsList.map((tab) => (
          <div key={tab.id} className="relative">
            {tab.extraLabel && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[8px] font-black text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-250 uppercase tracking-widest whitespace-nowrap animate-pulse">
                {tab.extraLabel}
              </span>
            )}
            <button
              onClick={() => setFilterStatus(filterStatus === tab.id ? "all" : tab.id)}
              className={getTabStyle(tab.id, tab.colorClass)}
            >
              <span>{tab.label.toUpperCase()}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-extrabold ${
                filterStatus === tab.id
                  ? "bg-white/20 text-white"
                  : tab.colorClass === "slate"
                  ? "bg-slate-105 text-slate-600"
                  : tab.colorClass === "amber"
                  ? "bg-amber-100 text-amber-800"
                  : tab.colorClass === "blue"
                  ? "bg-blue-100 text-blue-800"
                  : tab.colorClass === "purple"
                  ? "bg-purple-100 text-purple-800"
                  : tab.colorClass === "emerald"
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-rose-100 text-rose-800"
              }`}>
                {tab.count}
              </span>
            </button>
          </div>
        ))}
      </div>

      {/* WORKFLOW VIEW */}
      <div className="flex-1 overflow-auto p-6">
        
        {viewMode === "kanban" ? (
          /* KANBAN BOARD */
          <div className="grid grid-cols-1 md:grid-flow-col auto-cols-fr gap-4 h-full items-start">
            {columns.filter(col => filterStatus === "all" || col.id === filterStatus).map(col => {
              const items = filteredModelings.filter(m => m.status === col.id);
              return (
                <div key={col.id} className="bg-slate-100 border border-slate-200/80 rounded-2xl p-3 flex flex-col max-h-full min-w-[200px]">
                  <div className="flex justify-between items-center mb-3 border-b border-slate-200 pb-2">
                    <span className="font-extrabold text-slate-700 uppercase tracking-wider text-[10px]">{col.label}</span>
                    <span className="bg-slate-200 text-slate-650 px-2 py-0.5 rounded-full font-bold text-[10px]">{items.length}</span>
                  </div>

                  <div className="space-y-3 overflow-y-auto custom-scrollbar flex-1 pb-4">
                    {items.map(item => (
                      <div
                        key={item.id}
                        className={`rounded-xl border p-3 shadow-sm transition-all space-y-3 ${
                          item.status === "pendente_aprovacao"
                            ? user?.role === "master"
                              ? "bg-white border-slate-200 hover:shadow-md"
                              : "bg-slate-50 border-slate-200/60 opacity-80"
                            : item.status === "rejeitado"
                            ? "bg-rose-50/30 border-rose-100"
                            : "bg-white border-slate-200 hover:shadow-md"
                        }`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-mono font-bold text-slate-850 truncate" title={item.sku}>{item.sku}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            {item.prioridade === "urgente" && (
                              <span className="text-[8px] font-black bg-red-100 text-red-800 border border-red-200 px-1 py-0.2 rounded uppercase animate-pulse">Urgente</span>
                            )}
                            {item.prioridade === "alta" && (
                              <span className="text-[8px] font-black bg-orange-100 text-orange-850 border border-orange-200 px-1 py-0.2 rounded uppercase">Alta</span>
                            )}
                            {item.prioridade === "media" && (
                              <span className="text-[8px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-1 py-0.2 rounded uppercase">Média</span>
                            )}
                            {item.prioridade === "baixa" && (
                              <span className="text-[8px] font-medium bg-slate-50 text-slate-400 border border-slate-100 px-1 py-0.2 rounded uppercase">Baixa</span>
                            )}
                            <div className="flex gap-1 ml-1">
                            {/* Hide edit and delete for non-masters if status is pendente_aprovacao, unless they are modelista / owner */}
                            {(!(item.status === "pendente_aprovacao" && user?.role !== "master") || user?.role === "modelista" || item.responsavel === user?.id) && (
                              <>
                                <button
                                  onClick={() => handleOpenEdit(item)}
                                  className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded"
                                >
                                  <Edit3 size={11} />
                                </button>
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-650 rounded"
                                >
                                  <Trash2 size={11} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                        <div>
                          <h4 className="font-bold text-slate-800 text-[11px] leading-tight">{item.nome_produto}</h4>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className="text-[9px] text-slate-400 bg-slate-100 border border-slate-200 px-1 py-0.2 rounded uppercase font-semibold">{item.tipo_joia || item.categoria || "Anel"}</span>
                            {item.categoria && (
                              <span className="text-[9px] text-blue-600 bg-blue-50 border border-blue-100 px-1 py-0.2 rounded font-semibold">{item.categoria}</span>
                            )}
                          </div>
                        </div>

                        {item.observacoes && (
                          <p className="text-[10px] text-slate-500 italic line-clamp-2 bg-slate-50 p-2 rounded border border-slate-100">
                            "{item.observacoes}"
                          </p>
                        )}

                        {/* Files display */}
                        {(() => {
                          const arquivosList = getArquivosArray(item.arquivos);
                          return arquivosList.length > 0 && (
                            <div className="flex flex-wrap gap-1 border-t border-slate-100 pt-2">
                              {arquivosList.map((filename, fileIdx) => {
                                const fileUrl = pb.files.getURL(item, filename);
                                return (
                                  <a
                                    key={fileIdx}
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-bold border border-blue-100 flex items-center gap-0.5 hover:bg-blue-100 transition-colors"
                                  >
                                    <FileText size={10} /> Link 3D
                                  </a>
                                );
                              })}
                            </div>
                          );
                        })()}

                        {/* Catalog Sync Indicator */}
                        <div className="flex justify-between items-center border-t border-slate-100 pt-2 mt-2">
                          <span className="text-[9px] text-slate-400">Resp: <strong>{item.expand?.responsavel?.name || item.responsavel || "-"}</strong></span>
                          
                          {/* Only show sync catalog button if not pending and not rejected */}
                          {item.status !== "pendente_aprovacao" && item.status !== "rejeitado" && (
                            <button
                              onClick={() => handleToggleCatalogLink(item)}
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center gap-0.5 transition-all ${
                                item.produto_cadastrado
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                  : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
                              }`}
                            >
                              <Link size={9} /> {item.produto_cadastrado ? `Catálogo: ${item.sku_catalogo}` : "Vincular Comercial"}
                            </button>
                          )}
                        </div>

                        {/* Approval buttons inside the card */}
                        {item.status === "pendente_aprovacao" && user?.role === "master" && (
                          <div className="bg-slate-100/80 border border-slate-200 rounded-lg p-1.5 flex flex-col gap-1.5 mt-2">
                            <div className="text-[9px] text-slate-500 font-bold text-center">Decisão de Aprovação:</div>
                            <div className="flex gap-1.5 justify-center">
                              <button
                                onClick={() => updateStatus(item.id, "solicitado")}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded py-1 px-1.5 text-[9px] font-bold flex items-center justify-center gap-0.5 shadow-sm transition-all"
                              >
                                <CheckCircle2 size={10} /> Aprovar
                              </button>
                              <button
                                onClick={() => updateStatus(item.id, "rejeitado")}
                                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white rounded py-1 px-1.5 text-[9px] font-bold flex items-center justify-center gap-0.5 shadow-sm transition-all"
                              >
                                <X size={10} /> Rejeitar
                              </button>
                            </div>
                          </div>
                        )}

                        {item.status === "pendente_aprovacao" && user?.role !== "master" && (
                          <div className="bg-slate-200/50 border border-slate-300/60 rounded-lg p-1 text-center text-[9px] text-slate-550 font-bold italic mt-2 flex items-center justify-center gap-1">
                            <AlertCircle size={10} className="text-slate-400 animate-pulse" /> Aguardando aprovação
                          </div>
                        )}

                        {item.status === "rejeitado" && user?.role === "master" && (
                          <div className="bg-red-50 border border-red-100 rounded-lg p-1.5 flex flex-col gap-1.5 mt-2">
                            <div className="text-[9px] text-red-700 font-bold text-center">Solicitação Rejeitada:</div>
                            <div className="flex gap-1.5 justify-center">
                              <button
                                onClick={() => updateStatus(item.id, "solicitado")}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded py-1 px-1.5 text-[9px] font-bold flex items-center justify-center gap-0.5 shadow-sm transition-all"
                              >
                                <CheckCircle2 size={10} /> Aprovar
                              </button>
                              <button
                                onClick={() => updateStatus(item.id, "pendente_aprovacao")}
                                className="flex-1 bg-slate-600 hover:bg-slate-700 text-white rounded py-1 px-1.5 text-[9px] font-bold flex items-center justify-center gap-0.5 shadow-sm transition-all"
                              >
                                Reabrir
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Move column quick buttons */}
                        {item.status !== "pendente_aprovacao" && item.status !== "rejeitado" && (
                          <div className="flex gap-1 pt-2 mt-2 border-t border-slate-100">
                            {columns
                              .filter(c => c.id !== item.status && c.id !== "pendente_aprovacao" && c.id !== "rejeitado")
                              .map(col => (
                                <button
                                  key={col.id}
                                  onClick={() => updateStatus(item.id, col.id)}
                                  className="px-1 py-0.5 rounded bg-slate-50 hover:bg-slate-150 border border-slate-200 text-[8px] font-semibold text-slate-500"
                                  title={`Mover para ${col.label}`}
                                >
                                  {col.label.split(" ")[0]}
                                </button>
                              ))}
                          </div>
                        )}

                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className="text-center text-slate-400 text-[10px] py-8 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                        Nenhum item nesta etapa.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* GROUPED LIST TABLES BY STATUS */
          <div className="space-y-8">
            {columns.filter(col => filterStatus === "all" || col.id === filterStatus).map(col => {
              const items = filteredModelings.filter(m => m.status === col.id);

              return (
                <div key={col.id} className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                    <span className={`px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${col.color}`}>
                      {col.label}
                    </span>
                    <span className="bg-slate-200 text-slate-650 px-2 py-0.5 rounded-full font-bold text-[9px]">
                      {items.length} {items.length === 1 ? "item" : "itens"}
                    </span>
                  </div>

                  {items.length > 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className={`${HEADER_STYLES[col.id] || "bg-slate-50 text-slate-500 border-slate-200"} border-b text-[10px] font-extrabold uppercase tracking-wider`}>
                            <th className="py-3 px-3 w-28 text-white">SKU</th>
                            <th className="py-3 px-3 w-48 text-white">Nome do Produto / Modelo</th>
                            <th className="py-3 px-3 w-24 text-white">Tipo de Joia</th>
                            <th className="py-3 px-3 w-24 text-white">Categoria</th>
                            <th className="py-3 px-3 w-20 text-white">Prioridade</th>
                            <th className="py-3 px-3 w-24 text-white">Responsável</th>
                            <th className="py-3 px-3 w-60 text-white">Observações</th>
                            <th className="py-3 px-3 w-44 text-white">Arquivos 3D</th>
                            <th className="py-3 px-3 w-24 text-white">M. Livre?</th>
                            <th className="py-3 px-3 w-32 text-white">Link do Produto</th>
                            <th className="py-3 px-3 w-28 text-white">Status</th>
                            <th className="py-3 px-3 w-28 text-white">Comercial</th>
                            <th className="py-3 px-3 text-right w-20 text-white">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {items.map(item => (
                            <tr
                              key={item.id}
                              className={`transition-colors ${
                                item.status === "pendente_aprovacao"
                                  ? user?.role === "master"
                                    ? "hover:bg-slate-50/55"
                                    : "bg-slate-50/60 opacity-80"
                                  : item.status === "rejeitado"
                                  ? "bg-rose-50/30 hover:bg-rose-50/50"
                                  : "hover:bg-slate-50/55"
                              }`}
                            >
                              <td className="p-3 font-mono font-bold text-slate-800">{item.sku}</td>
                              <td className="p-3">
                                <div className="font-bold text-slate-800">{item.nome_produto}</div>
                                {item.modelo && (
                                  <div className="mt-0.5 text-[9px] text-purple-700 font-bold bg-purple-50 border border-purple-100 rounded px-1.5 py-0.5 w-fit">
                                    Modelo: {item.modelo}
                                  </div>
                                )}
                              </td>
                              <td className="p-3">
                                <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200 font-bold">
                                  {item.tipo_joia || item.categoria || "Anel"}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-150 font-bold">
                                  {item.categoria || "Outros"}
                                </span>
                              </td>
                              <td className="p-3">
                                {item.prioridade === "urgente" ? (
                                  <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 font-black uppercase text-[9px] tracking-wider animate-pulse">
                                    Urgente
                                  </span>
                                ) : item.prioridade === "alta" ? (
                                  <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded border border-orange-200 font-black uppercase text-[9px] tracking-wider">
                                    Alta
                                  </span>
                                ) : item.prioridade === "media" ? (
                                  <span className="bg-slate-50 text-slate-600 px-2 py-0.5 rounded border border-slate-200 font-bold uppercase text-[9px]">
                                    Média
                                  </span>
                                ) : (
                                  <span className="bg-slate-50/50 text-slate-400 px-2 py-0.5 rounded border border-slate-100 font-medium uppercase text-[9px]">
                                    Baixa
                                  </span>
                                )}
                              </td>
                              <td className="p-3 font-bold text-slate-650">{item.expand?.responsavel?.name || item.responsavel || "-"}</td>
                              
                              <td className="p-3 font-medium text-slate-550 leading-relaxed max-w-xs truncate" title={item.observacoes || ""}>
                                {item.observacoes ? (
                                  <span className="italic">"{item.observacoes}"</span>
                                ) : (
                                  <span className="text-slate-350 italic">Sem observações</span>
                                )}
                              </td>

                              <td className="p-3">
                                <div className="space-y-1.5">
                                  {(() => {
                                    const arquivosList = getArquivosArray(item.arquivos);
                                    return arquivosList.length > 0 ? (
                                      <div className="flex flex-col gap-1 max-w-[200px]">
                                        {arquivosList.map((filename, fileIdx) => {
                                          const fileUrl = pb.files.getURL(item, filename);
                                          return (
                                            <a
                                              key={fileIdx}
                                              href={fileUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-200 text-[10px] font-bold flex items-center gap-1 transition-all"
                                              title={filename}
                                            >
                                              <FileText size={11} /> {filename.length > 18 ? filename.substring(0, 15) + "..." : filename}
                                            </a>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span className="text-slate-400 italic">Nenhum arquivo</span>
                                    );
                                  })()}
                                  
                                  {/* Only allow upload if not pending/rejected OR if user is master OR modelista OR owner */}
                                  {(!(item.status === "pendente_aprovacao" || item.status === "rejeitado") || user?.role === "master" || user?.role === "modelista" || item.responsavel === user?.id) && (
                                    <label className="w-fit bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded px-2 py-1 flex items-center gap-1 text-[9px] font-extrabold cursor-pointer text-slate-650 transition-all select-none">
                                      <Upload size={10} /> Enviar Arquivo
                                      <input
                                        type="file"
                                        className="hidden"
                                        onChange={async (e) => {
                                           const file = e.target.files[0];
                                           if (!file) return;
                                           const formData = new FormData();
                                           formData.append("arquivos+", file);
                                           try {
                                             const updated = await pb.collection("modelagens").update(item.id, formData);
                                             setModelings(prev => prev.map(m => m.id === item.id ? updated : m));
                                             alert("Arquivo enviado com sucesso!");
                                           } catch (err) {
                                             alert("Erro ao enviar: " + err.message);
                                           }
                                         }}
                                       />
                                    </label>
                                  )}
                                </div>
                              </td>
                               
                               <td className="p-3">
                                 <label className="relative inline-flex items-center cursor-pointer">
                                   <input
                                     type="checkbox"
                                     className="sr-only peer"
                                     checked={!!item.mercado_livre}
                                     disabled={user?.role !== "master" && (item.status === "pendente_aprovacao" || item.status === "rejeitado")}
                                     onChange={async () => {
                                       try {
                                         const updated = await pb.collection("modelagens").update(item.id, {
                                           mercado_livre: !item.mercado_livre
                                         });
                                         setModelings(prev => prev.map(m => m.id === item.id ? updated : m));
                                       } catch (err) {
                                         alert("Erro ao atualizar Mercado Livre: " + err.message);
                                       }
                                     }}
                                   />
                                   <div className="w-8 h-4 bg-slate-200 rounded-full peer peer-focus:ring-0 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-600"></div>
                                   <span className="ml-2 text-[10px] font-bold text-slate-650">
                                     {item.mercado_livre ? "Sim" : "Não"}
                                   </span>
                                 </label>
                               </td>

                               <td className="p-3">
                                 {item.link_produto ? (
                                   <a
                                     href={item.link_produto}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-blue-700 rounded border border-slate-350 text-[10px] font-bold inline-flex items-center gap-1 transition-all"
                                   >
                                     <Link size={10} /> Link da Loja
                                   </a>
                                 ) : (
                                   <span className="text-slate-400 italic">Sem link</span>
                                 )}
                               </td>
                              
                              <td className="p-3">
                                <select
                                  value={item.status}
                                  disabled={user?.role !== "master" && (item.status === "pendente_aprovacao" || item.status === "rejeitado")}
                                  onChange={(e) => updateStatus(item.id, e.target.value)}
                                  className={`p-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border outline-none bg-white cursor-pointer ${
                                    item.status === "solicitado"
                                      ? "text-blue-700 border-blue-300 bg-blue-50"
                                      : item.status === "em_revisao"
                                      ? "text-amber-700 border-amber-300 bg-amber-50"
                                      : item.status === "aguardando_anuncio"
                                      ? "text-purple-700 border-purple-300 bg-purple-50"
                                      : item.status === "anunciado"
                                      ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                                      : item.status === "pendente_aprovacao"
                                      ? "text-slate-600 border-slate-300 bg-slate-100"
                                      : "text-red-700 border-red-300 bg-red-50"
                                  }`}
                                >
                                  {(user?.role === "master" || item.status === "pendente_aprovacao") && (
                                    <option value="pendente_aprovacao">Pendente</option>
                                  )}
                                  <option value="solicitado">Solicitado</option>
                                  <option value="em_revisao">Em Revisão</option>
                                  <option value="aguardando_anuncio">Aguardando Anúncio</option>
                                  <option value="anunciado">Anunciado</option>
                                  {(user?.role === "master" || item.status === "rejeitado") && (
                                    <option value="rejeitado">Rejeitado</option>
                                  )}
                                </select>
                              </td>
                              
                              <td className="p-3">
                                {item.status === "pendente_aprovacao" || item.status === "rejeitado" ? (
                                  <span className="text-slate-400 italic">Aguardando aprovação</span>
                                ) : item.produto_cadastrado ? (
                                  <div className="flex flex-col items-start gap-1">
                                    <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded text-[9px] font-bold">
                                      Cadastrado
                                    </span>
                                    <span className="font-mono text-[9px] font-bold text-slate-500">SKU: {item.sku_catalogo}</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic text-[10px]">Não Cadastrado</span>
                                )}
                              </td>
                              
                              <td className="p-3 text-right">
                                <div className="flex justify-end gap-1.5">
                                  {item.status === "pendente_aprovacao" && user?.role !== "master" && user?.role !== "modelista" && item.responsavel !== user?.id ? (
                                    <span className="text-slate-400 italic text-[10px] font-bold pr-2 flex items-center gap-0.5">
                                      <AlertCircle size={10} /> Bloqueado
                                    </span>
                                  ) : (
                                    <>
                                      {item.status === "pendente_aprovacao" && user?.role === "master" && (
                                        <>
                                          <button
                                            onClick={() => updateStatus(item.id, "solicitado")}
                                            className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded border border-emerald-200 bg-white"
                                            title="Aprovar"
                                          >
                                            <CheckCircle2 size={11} />
                                          </button>
                                          <button
                                            onClick={() => updateStatus(item.id, "rejeitado")}
                                            className="p-1.5 hover:bg-red-50 text-red-600 rounded border border-red-200 bg-white"
                                            title="Rejeitar"
                                          >
                                            <X size={11} />
                                          </button>
                                        </>
                                      )}
                                      {item.status === "rejeitado" && user?.role === "master" && (
                                        <button
                                          onClick={() => updateStatus(item.id, "solicitado")}
                                          className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded border border-emerald-200 bg-white"
                                          title="Aprovar"
                                        >
                                          <CheckCircle2 size={11} />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleOpenEdit(item)}
                                        className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-slate-700 rounded border border-slate-200 bg-white"
                                        title="Editar"
                                      >
                                        <Edit3 size={11} />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(item.id)}
                                        className="p-1.5 hover:bg-red-50 text-red-600 rounded border border-red-200 bg-white"
                                        title="Apagar"
                                      >
                                        <Trash2 size={11} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400 text-[10px] py-4 border border-dashed border-slate-200 rounded-xl bg-white/50">
                      Nenhum item nesta etapa.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* DIALOG FOR CREATE/EDIT */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">
                {editingItem ? "Editar Modelagem CAD" : "Nova Solicitação de Modelagem"}
              </h3>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4 font-bold">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">SKU do Protótipo</label>
                <input
                  type="text" required
                  disabled={!editingItem}
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  placeholder="Ex: AN-TRI-Z-10"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Produto *</label>
                <input
                  type="text" required
                  value={form.nome_produto}
                  onChange={(e) => setForm({ ...form, nome_produto: e.target.value })}
                  placeholder="Ex: Anel Trindade Ouro"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tipo de Joia</label>
                  <select
                    value={form.tipo_joia}
                    onChange={(e) => setForm({ ...form, tipo_joia: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="Anel">Anel</option>
                    <option value="Brinco">Brinco</option>
                    <option value="Colar">Colar</option>
                    <option value="Pulseira">Pulseira</option>
                    <option value="Pingente">Pingente</option>
                    <option value="Tornozeleira">Tornozeleira</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Categoria</label>
                  <select
                    value={form.categoria}
                    onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="Formatura">Formatura</option>
                    <option value="Pet">Pet</option>
                    <option value="Coleção">Coleção</option>
                    <option value="Sacro">Sacro</option>
                    <option value="Espiritualista">Espiritualista</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Prioridade</label>
                  <select
                    value={form.prioridade}
                    onChange={(e) => setForm({ ...form, prioridade: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="urgente">Urgente</option>
                    <option value="alta">Alta</option>
                    <option value="media">Média</option>
                    <option value="baixa">Baixa</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Modelo</label>
                  <input
                    type="text"
                    value={form.modelo}
                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                    placeholder="Ex: Escapulário, Solitário"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Responsável / Designer</label>
                  <input
                    type="text"
                    disabled
                    value={editingItem ? (editingItem.expand?.responsavel?.name || editingItem.responsavel || "-") : (user?.name || "")}
                    className="w-full p-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 outline-none select-none font-bold text-xs"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 py-2 border-t border-b border-slate-100 my-2">
                <input
                  type="checkbox"
                  id="dialog-mercado-livre"
                  checked={form.mercado_livre}
                  onChange={(e) => setForm({ ...form, mercado_livre: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-slate-350 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="dialog-mercado-livre" className="text-[11px] font-bold text-slate-700 cursor-pointer select-none">
                  Vender no Mercado Livre? (Tag Comercial)
                </label>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Link Relacionado (Ex: Pinterest, Referência)</label>
                <input
                  type="url"
                  value={form.link_produto}
                  onChange={(e) => setForm({ ...form, link_produto: e.target.value })}
                  placeholder="https://..."
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Anexar Arquivos 3D ou Renders (STL, OBJ, PNG)</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setForm({ ...form, arquivos: e.target.files })}
                  className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Instruções / Observações</label>
                <textarea
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3}
                  placeholder="Informações adicionais de pedras, peso estimado ou detalhes..."
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-semibold"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm transition-all"
                >
                  Salvar Solicitação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
